/**
 * @yada/i18n — Country Context
 *
 * Country selection is INDEPENDENT of language selection.
 * A user can have their UI in English (locale = 'en') but their country
 * set to Poland ('PL'), which unlocks country-specific features and data.
 *
 * Country context is broadcast by the spine (shell) to all plugin apps via
 * AppHostApi.locale.country, and via the React CountryContext provider.
 */

// ── Types ─────────────────────────────────────────────────────────────────────

/** ISO 3166-1 alpha-2 country codes for supported countries. */
export type SupportedCountry =
  | 'AD'
  | 'AE'
  | 'AF'
  | 'AG'
  | 'AI'
  | 'AL'
  | 'AM'
  | 'AO'
  | 'AQ'
  | 'AR'
  | 'AS'
  | 'AT'
  | 'AU'
  | 'AW'
  | 'AX'
  | 'AZ'
  | 'BA'
  | 'BB'
  | 'BD'
  | 'BE'
  | 'BF'
  | 'BG'
  | 'BH'
  | 'BI'
  | 'BJ'
  | 'BL'
  | 'BM'
  | 'BN'
  | 'BO'
  | 'BQ'
  | 'BR'
  | 'BS'
  | 'BT'
  | 'BV'
  | 'BW'
  | 'BY'
  | 'BZ'
  | 'CA'
  | 'CC'
  | 'CD'
  | 'CF'
  | 'CG'
  | 'CH'
  | 'CI'
  | 'CK'
  | 'CL'
  | 'CM'
  | 'CN'
  | 'CO'
  | 'CR'
  | 'CU'
  | 'CV'
  | 'CW'
  | 'CX'
  | 'CY'
  | 'CZ'
  | 'DE'
  | 'DJ'
  | 'DK'
  | 'DM'
  | 'DO'
  | 'DZ'
  | 'EC'
  | 'EE'
  | 'EG'
  | 'EH'
  | 'ER'
  | 'ES'
  | 'ET'
  | 'FI'
  | 'FJ'
  | 'FK'
  | 'FM'
  | 'FO'
  | 'FR'
  | 'GA'
  | 'GB'
  | 'GD'
  | 'GE'
  | 'GF'
  | 'GG'
  | 'GH'
  | 'GI'
  | 'GL'
  | 'GM'
  | 'GN'
  | 'GP'
  | 'GQ'
  | 'GR'
  | 'GS'
  | 'GT'
  | 'GU'
  | 'GW'
  | 'GY'
  | 'HK'
  | 'HM'
  | 'HN'
  | 'HR'
  | 'HT'
  | 'HU'
  | 'ID'
  | 'IE'
  | 'IL'
  | 'IM'
  | 'IN'
  | 'IO'
  | 'IQ'
  | 'IR'
  | 'IS'
  | 'IT'
  | 'JE'
  | 'JM'
  | 'JO'
  | 'JP'
  | 'KE'
  | 'KG'
  | 'KH'
  | 'KI'
  | 'KM'
  | 'KN'
  | 'KP'
  | 'KR'
  | 'KW'
  | 'KY'
  | 'KZ'
  | 'LA'
  | 'LB'
  | 'LC'
  | 'LI'
  | 'LK'
  | 'LR'
  | 'LS'
  | 'LT'
  | 'LU'
  | 'LV'
  | 'LY'
  | 'MA'
  | 'MC'
  | 'MD'
  | 'ME'
  | 'MF'
  | 'MG'
  | 'MH'
  | 'MK'
  | 'ML'
  | 'MM'
  | 'MN'
  | 'MO'
  | 'MP'
  | 'MQ'
  | 'MR'
  | 'MS'
  | 'MT'
  | 'MU'
  | 'MV'
  | 'MW'
  | 'MX'
  | 'MY'
  | 'MZ'
  | 'NA'
  | 'NC'
  | 'NE'
  | 'NF'
  | 'NG'
  | 'NI'
  | 'NL'
  | 'NO'
  | 'NP'
  | 'NR'
  | 'NU'
  | 'NZ'
  | 'OM'
  | 'PA'
  | 'PE'
  | 'PF'
  | 'PG'
  | 'PH'
  | 'PK'
  | 'PL'
  | 'PM'
  | 'PN'
  | 'PR'
  | 'PS'
  | 'PT'
  | 'PW'
  | 'PY'
  | 'QA'
  | 'RE'
  | 'RO'
  | 'RS'
  | 'RU'
  | 'RW'
  | 'SA'
  | 'SB'
  | 'SC'
  | 'SD'
  | 'SE'
  | 'SG'
  | 'SH'
  | 'SI'
  | 'SJ'
  | 'SK'
  | 'SL'
  | 'SM'
  | 'SN'
  | 'SO'
  | 'SR'
  | 'SS'
  | 'ST'
  | 'SV'
  | 'SX'
  | 'SY'
  | 'SZ'
  | 'TC'
  | 'TD'
  | 'TF'
  | 'TG'
  | 'TH'
  | 'TJ'
  | 'TK'
  | 'TL'
  | 'TM'
  | 'TN'
  | 'TO'
  | 'TR'
  | 'TT'
  | 'TV'
  | 'TW'
  | 'TZ'
  | 'UA'
  | 'UG'
  | 'UM'
  | 'US'
  | 'UY'
  | 'UZ'
  | 'VA'
  | 'VC'
  | 'VE'
  | 'VG'
  | 'VI'
  | 'VN'
  | 'VU'
  | 'WF'
  | 'WS'
  | 'YE'
  | 'YT'
  | 'ZA'
  | 'ZM'
  | 'ZW';

/** Metadata about a country for display in UI selectors. */
export interface CountryOption {
  /** ISO 3166-1 alpha-2 */
  code: SupportedCountry;
  /** Country name in English */
  englishName: string;
  /** Country name in the native language */
  nativeName: string;
  /** Unicode flag emoji */
  flag: string;
  /** BCP-47 tag for the primary language of this country (for default locale suggestion) */
  primaryLocale?: string;
  /** ISO 4217 currency code */
  currency: string;
  /** Currency symbol */
  currencySymbol: string;
  /** Date format convention (display only) */
  dateFormat: 'DMY' | 'MDY' | 'YMD';
  /** Whether decimal separator is comma (EU style) vs period (US style) */
  decimalSeparator: ',' | '.';
}

/**
 * Country-specific feature flags.
 *
 * These are injected by the spine into every app's host API under
 * `host.locale.countryFeatures`. Apps can gate functionality behind these
 * flags without knowing anything about specific countries.
 *
 * To add a new flag: extend this interface and add values to COUNTRY_FEATURES.
 */
export interface CountryFeatures {
  /**
   * Poland: KSeF (Krajowy System e-Faktur) — mandatory electronic invoicing.
   * When true, invoicing tools should offer KSeF integration.
   */
  ksef: boolean;

  /**
   * Whether the country uses the Euro (€) as its official currency.
   * Useful for invoicing / finance apps.
   */
  eurozone: boolean;

  /**
   * Whether the country is in the EU customs union (affects import labelling).
   */
  euMember: boolean;

  /**
   * Whether the country drives on the left side of the road.
   * Relevant for navigation / transport apps.
   */
  leftHandTraffic: boolean;

  /**
   * Default measurement system.
   * Relevant for recipe / fitness / health apps.
   */
  measurementSystem: 'metric' | 'imperial';

  /**
   * First day of the week as used in this country's calendar conventions.
   * 0 = Sunday, 1 = Monday.
   */
  firstDayOfWeek: 0 | 1;
}

/**
 * Full country context object provided to apps.
 * Combines the static CountryOption metadata with dynamic CountryFeatures flags.
 */
export interface CountryContext {
  country: CountryOption;
  features: CountryFeatures;
}

// ── Country registry ──────────────────────────────────────────────────────────

/** Full metadata for every supported country. */
export const SUPPORTED_COUNTRIES: CountryOption[] = [
  { code: 'PL', englishName: 'Poland',          nativeName: 'Polska',       flag: '🇵🇱', primaryLocale: 'pl', currency: 'PLN', currencySymbol: 'zł', dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'GB', englishName: 'United Kingdom',   nativeName: 'United Kingdom', flag: '🇬🇧', primaryLocale: 'en', currency: 'GBP', currencySymbol: '£',  dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'US', englishName: 'United States',    nativeName: 'United States', flag: '🇺🇸', primaryLocale: 'en', currency: 'USD', currencySymbol: '$',  dateFormat: 'MDY', decimalSeparator: '.' },
  { code: 'DE', englishName: 'Germany',          nativeName: 'Deutschland',  flag: '🇩🇪', primaryLocale: 'de', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'FR', englishName: 'France',           nativeName: 'France',       flag: '🇫🇷', primaryLocale: 'fr', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'ES', englishName: 'Spain',            nativeName: 'España',       flag: '🇪🇸', primaryLocale: 'es', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'IT', englishName: 'Italy',            nativeName: 'Italia',       flag: '🇮🇹', primaryLocale: 'it', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'NL', englishName: 'Netherlands',      nativeName: 'Nederland',    flag: '🇳🇱', primaryLocale: 'nl', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'SE', englishName: 'Sweden',           nativeName: 'Sverige',      flag: '🇸🇪', primaryLocale: 'sv', currency: 'SEK', currencySymbol: 'kr', dateFormat: 'YMD', decimalSeparator: ',' },
  { code: 'NO', englishName: 'Norway',           nativeName: 'Norge',        flag: '🇳🇴', primaryLocale: 'no', currency: 'NOK', currencySymbol: 'kr', dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'DK', englishName: 'Denmark',          nativeName: 'Danmark',      flag: '🇩🇰', primaryLocale: 'da', currency: 'DKK', currencySymbol: 'kr', dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'FI', englishName: 'Finland',          nativeName: 'Suomi',        flag: '🇫🇮', primaryLocale: 'fi', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'PT', englishName: 'Portugal',         nativeName: 'Portugal',     flag: '🇵🇹', primaryLocale: 'pt', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'CZ', englishName: 'Czech Republic',   nativeName: 'Česká republika', flag: '🇨🇿', primaryLocale: 'cs', currency: 'CZK', currencySymbol: 'Kč', dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'SK', englishName: 'Slovakia',         nativeName: 'Slovensko',    flag: '🇸🇰', primaryLocale: 'sk', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'HU', englishName: 'Hungary',          nativeName: 'Magyarország', flag: '🇭🇺', primaryLocale: 'hu', currency: 'HUF', currencySymbol: 'Ft', dateFormat: 'YMD', decimalSeparator: ',' },
  { code: 'RO', englishName: 'Romania',          nativeName: 'România',      flag: '🇷🇴', primaryLocale: 'ro', currency: 'RON', currencySymbol: 'lei', dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'AT', englishName: 'Austria',          nativeName: 'Österreich',   flag: '🇦🇹', primaryLocale: 'de', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'CH', englishName: 'Switzerland',      nativeName: 'Schweiz',      flag: '🇨🇭', primaryLocale: 'de', currency: 'CHF', currencySymbol: 'Fr', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BE', englishName: 'Belgium',          nativeName: 'België',       flag: '🇧🇪', primaryLocale: 'nl', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'IE', englishName: 'Ireland',          nativeName: 'Ireland',      flag: '🇮🇪', primaryLocale: 'en', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'CY', englishName: 'Cyprus',           nativeName: 'Κύπρος',       flag: '🇨🇾', primaryLocale: 'el', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'EE', englishName: 'Estonia',          nativeName: 'Eesti',        flag: '🇪🇪', primaryLocale: 'et', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'GR', englishName: 'Greece',           nativeName: 'Ελλάδα',       flag: '🇬🇷', primaryLocale: 'el', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'LV', englishName: 'Latvia',           nativeName: 'Latvija',      flag: '🇱🇻', primaryLocale: 'lv', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'LT', englishName: 'Lithuania',        nativeName: 'Lietuva',      flag: '🇱🇹', primaryLocale: 'lt', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'LU', englishName: 'Luxembourg',       nativeName: 'Lëtzebuerg',   flag: '🇱🇺', primaryLocale: 'fr', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'MT', englishName: 'Malta',            nativeName: 'Malta',        flag: '🇲🇹', primaryLocale: 'mt', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'SI', englishName: 'Slovenia',         nativeName: 'Slovenija',    flag: '🇸🇮', primaryLocale: 'sl', currency: 'EUR', currencySymbol: '€',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'UA', englishName: 'Ukraine',          nativeName: 'Україна',      flag: '🇺🇦', primaryLocale: 'uk', currency: 'UAH', currencySymbol: '₴',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'BR', englishName: 'Brazil',           nativeName: 'Brasil',       flag: '🇧🇷', primaryLocale: 'pt', currency: 'BRL', currencySymbol: 'R$', dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'MX', englishName: 'Mexico',           nativeName: 'México',       flag: '🇲🇽', primaryLocale: 'es', currency: 'MXN', currencySymbol: '$',  dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'AR', englishName: 'Argentina',        nativeName: 'Argentina',    flag: '🇦🇷', primaryLocale: 'es', currency: 'ARS', currencySymbol: '$',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'CL', englishName: 'Chile',            nativeName: 'Chile',        flag: '🇨🇱', primaryLocale: 'es', currency: 'CLP', currencySymbol: '$',  dateFormat: 'DMY', decimalSeparator: ',' },
  { code: 'CA', englishName: 'Canada',           nativeName: 'Canada',       flag: '🇨🇦', primaryLocale: 'en', currency: 'CAD', currencySymbol: '$',  dateFormat: 'MDY', decimalSeparator: '.' },
  { code: 'AU', englishName: 'Australia',        nativeName: 'Australia',    flag: '🇦🇺', primaryLocale: 'en', currency: 'AUD', currencySymbol: '$',  dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'NZ', englishName: 'New Zealand',      nativeName: 'New Zealand',  flag: '🇳🇿', primaryLocale: 'en', currency: 'NZD', currencySymbol: '$',  dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'JP', englishName: 'Japan',            nativeName: '日本',          flag: '🇯🇵', primaryLocale: 'ja', currency: 'JPY', currencySymbol: '¥',  dateFormat: 'YMD', decimalSeparator: '.' },
  { code: 'KR', englishName: 'South Korea',      nativeName: '대한민국',       flag: '🇰🇷', primaryLocale: 'ko', currency: 'KRW', currencySymbol: '₩',  dateFormat: 'YMD', decimalSeparator: '.' },
  { code: 'IN', englishName: 'India',            nativeName: 'India',        flag: '🇮🇳', primaryLocale: 'hi', currency: 'INR', currencySymbol: '₹',  dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'SG', englishName: 'Singapore',        nativeName: 'Singapore',    flag: '🇸🇬', primaryLocale: 'en', currency: 'SGD', currencySymbol: '$',  dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'AW', englishName: 'Aruba', nativeName: 'Aruba', flag: '🇦🇼', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'AF', englishName: 'Afghanistan', nativeName: 'Afghanistan', flag: '🇦🇫', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'AO', englishName: 'Angola', nativeName: 'Angola', flag: '🇦🇴', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'AI', englishName: 'Anguilla', nativeName: 'Anguilla', flag: '🇦🇮', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'AX', englishName: 'Åland Islands', nativeName: 'Åland Islands', flag: '🇦🇽', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'AL', englishName: 'Albania', nativeName: 'Albania', flag: '🇦🇱', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'AD', englishName: 'Andorra', nativeName: 'Andorra', flag: '🇦🇩', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'AE', englishName: 'United Arab Emirates', nativeName: 'United Arab Emirates', flag: '🇦🇪', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'AM', englishName: 'Armenia', nativeName: 'Armenia', flag: '🇦🇲', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'AS', englishName: 'American Samoa', nativeName: 'American Samoa', flag: '🇦🇸', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'AQ', englishName: 'Antarctica', nativeName: 'Antarctica', flag: '🇦🇶', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'TF', englishName: 'French Southern Territories', nativeName: 'French Southern Territories', flag: '🇹🇫', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'AG', englishName: 'Antigua and Barbuda', nativeName: 'Antigua and Barbuda', flag: '🇦🇬', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'AZ', englishName: 'Azerbaijan', nativeName: 'Azerbaijan', flag: '🇦🇿', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BI', englishName: 'Burundi', nativeName: 'Burundi', flag: '🇧🇮', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BJ', englishName: 'Benin', nativeName: 'Benin', flag: '🇧🇯', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BQ', englishName: 'Bonaire, Sint Eustatius and Saba', nativeName: 'Bonaire, Sint Eustatius and Saba', flag: '🇧🇶', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BF', englishName: 'Burkina Faso', nativeName: 'Burkina Faso', flag: '🇧🇫', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BD', englishName: 'Bangladesh', nativeName: 'Bangladesh', flag: '🇧🇩', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BG', englishName: 'Bulgaria', nativeName: 'Bulgaria', flag: '🇧🇬', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BH', englishName: 'Bahrain', nativeName: 'Bahrain', flag: '🇧🇭', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BS', englishName: 'Bahamas', nativeName: 'Bahamas', flag: '🇧🇸', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BA', englishName: 'Bosnia and Herzegovina', nativeName: 'Bosnia and Herzegovina', flag: '🇧🇦', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BL', englishName: 'Saint Barthélemy', nativeName: 'Saint Barthélemy', flag: '🇧🇱', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BY', englishName: 'Belarus', nativeName: 'Belarus', flag: '🇧🇾', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BZ', englishName: 'Belize', nativeName: 'Belize', flag: '🇧🇿', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BM', englishName: 'Bermuda', nativeName: 'Bermuda', flag: '🇧🇲', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BO', englishName: 'Bolivia, Plurinational State of', nativeName: 'Bolivia, Plurinational State of', flag: '🇧🇴', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BB', englishName: 'Barbados', nativeName: 'Barbados', flag: '🇧🇧', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BN', englishName: 'Brunei Darussalam', nativeName: 'Brunei Darussalam', flag: '🇧🇳', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BT', englishName: 'Bhutan', nativeName: 'Bhutan', flag: '🇧🇹', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BV', englishName: 'Bouvet Island', nativeName: 'Bouvet Island', flag: '🇧🇻', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'BW', englishName: 'Botswana', nativeName: 'Botswana', flag: '🇧🇼', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'CF', englishName: 'Central African Republic', nativeName: 'Central African Republic', flag: '🇨🇫', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'CC', englishName: 'Cocos (Keeling) Islands', nativeName: 'Cocos (Keeling) Islands', flag: '🇨🇨', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'CN', englishName: 'China', nativeName: 'China', flag: '🇨🇳', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'CI', englishName: 'Côte d\'Ivoire', nativeName: 'Côte d\'Ivoire', flag: '🇨🇮', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'CM', englishName: 'Cameroon', nativeName: 'Cameroon', flag: '🇨🇲', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'CD', englishName: 'Congo, The Democratic Republic of the', nativeName: 'Congo, The Democratic Republic of the', flag: '🇨🇩', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'CG', englishName: 'Congo', nativeName: 'Congo', flag: '🇨🇬', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'CK', englishName: 'Cook Islands', nativeName: 'Cook Islands', flag: '🇨🇰', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'CO', englishName: 'Colombia', nativeName: 'Colombia', flag: '🇨🇴', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'KM', englishName: 'Comoros', nativeName: 'Comoros', flag: '🇰🇲', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'CV', englishName: 'Cabo Verde', nativeName: 'Cabo Verde', flag: '🇨🇻', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'CR', englishName: 'Costa Rica', nativeName: 'Costa Rica', flag: '🇨🇷', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'CU', englishName: 'Cuba', nativeName: 'Cuba', flag: '🇨🇺', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'CW', englishName: 'Curaçao', nativeName: 'Curaçao', flag: '🇨🇼', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'CX', englishName: 'Christmas Island', nativeName: 'Christmas Island', flag: '🇨🇽', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'KY', englishName: 'Cayman Islands', nativeName: 'Cayman Islands', flag: '🇰🇾', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'DJ', englishName: 'Djibouti', nativeName: 'Djibouti', flag: '🇩🇯', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'DM', englishName: 'Dominica', nativeName: 'Dominica', flag: '🇩🇲', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'DO', englishName: 'Dominican Republic', nativeName: 'Dominican Republic', flag: '🇩🇴', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'DZ', englishName: 'Algeria', nativeName: 'Algeria', flag: '🇩🇿', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'EC', englishName: 'Ecuador', nativeName: 'Ecuador', flag: '🇪🇨', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'EG', englishName: 'Egypt', nativeName: 'Egypt', flag: '🇪🇬', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'ER', englishName: 'Eritrea', nativeName: 'Eritrea', flag: '🇪🇷', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'EH', englishName: 'Western Sahara', nativeName: 'Western Sahara', flag: '🇪🇭', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'ET', englishName: 'Ethiopia', nativeName: 'Ethiopia', flag: '🇪🇹', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'FJ', englishName: 'Fiji', nativeName: 'Fiji', flag: '🇫🇯', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'FK', englishName: 'Falkland Islands (Malvinas)', nativeName: 'Falkland Islands (Malvinas)', flag: '🇫🇰', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'FO', englishName: 'Faroe Islands', nativeName: 'Faroe Islands', flag: '🇫🇴', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'FM', englishName: 'Micronesia, Federated States of', nativeName: 'Micronesia, Federated States of', flag: '🇫🇲', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'GA', englishName: 'Gabon', nativeName: 'Gabon', flag: '🇬🇦', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'GE', englishName: 'Georgia', nativeName: 'Georgia', flag: '🇬🇪', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'GG', englishName: 'Guernsey', nativeName: 'Guernsey', flag: '🇬🇬', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'GH', englishName: 'Ghana', nativeName: 'Ghana', flag: '🇬🇭', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'GI', englishName: 'Gibraltar', nativeName: 'Gibraltar', flag: '🇬🇮', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'GN', englishName: 'Guinea', nativeName: 'Guinea', flag: '🇬🇳', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'GP', englishName: 'Guadeloupe', nativeName: 'Guadeloupe', flag: '🇬🇵', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'GM', englishName: 'Gambia', nativeName: 'Gambia', flag: '🇬🇲', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'GW', englishName: 'Guinea-Bissau', nativeName: 'Guinea-Bissau', flag: '🇬🇼', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'GQ', englishName: 'Equatorial Guinea', nativeName: 'Equatorial Guinea', flag: '🇬🇶', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'GD', englishName: 'Grenada', nativeName: 'Grenada', flag: '🇬🇩', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'GL', englishName: 'Greenland', nativeName: 'Greenland', flag: '🇬🇱', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'GT', englishName: 'Guatemala', nativeName: 'Guatemala', flag: '🇬🇹', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'GF', englishName: 'French Guiana', nativeName: 'French Guiana', flag: '🇬🇫', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'GU', englishName: 'Guam', nativeName: 'Guam', flag: '🇬🇺', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'GY', englishName: 'Guyana', nativeName: 'Guyana', flag: '🇬🇾', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'HK', englishName: 'Hong Kong', nativeName: 'Hong Kong', flag: '🇭🇰', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'HM', englishName: 'Heard Island and McDonald Islands', nativeName: 'Heard Island and McDonald Islands', flag: '🇭🇲', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'HN', englishName: 'Honduras', nativeName: 'Honduras', flag: '🇭🇳', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'HR', englishName: 'Croatia', nativeName: 'Croatia', flag: '🇭🇷', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'HT', englishName: 'Haiti', nativeName: 'Haiti', flag: '🇭🇹', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'ID', englishName: 'Indonesia', nativeName: 'Indonesia', flag: '🇮🇩', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'IM', englishName: 'Isle of Man', nativeName: 'Isle of Man', flag: '🇮🇲', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'IO', englishName: 'British Indian Ocean Territory', nativeName: 'British Indian Ocean Territory', flag: '🇮🇴', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'IR', englishName: 'Iran, Islamic Republic of', nativeName: 'Iran, Islamic Republic of', flag: '🇮🇷', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'IQ', englishName: 'Iraq', nativeName: 'Iraq', flag: '🇮🇶', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'IS', englishName: 'Iceland', nativeName: 'Iceland', flag: '🇮🇸', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'IL', englishName: 'Israel', nativeName: 'Israel', flag: '🇮🇱', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'JM', englishName: 'Jamaica', nativeName: 'Jamaica', flag: '🇯🇲', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'JE', englishName: 'Jersey', nativeName: 'Jersey', flag: '🇯🇪', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'JO', englishName: 'Jordan', nativeName: 'Jordan', flag: '🇯🇴', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'KZ', englishName: 'Kazakhstan', nativeName: 'Kazakhstan', flag: '🇰🇿', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'KE', englishName: 'Kenya', nativeName: 'Kenya', flag: '🇰🇪', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'KG', englishName: 'Kyrgyzstan', nativeName: 'Kyrgyzstan', flag: '🇰🇬', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'KH', englishName: 'Cambodia', nativeName: 'Cambodia', flag: '🇰🇭', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'KI', englishName: 'Kiribati', nativeName: 'Kiribati', flag: '🇰🇮', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'KN', englishName: 'Saint Kitts and Nevis', nativeName: 'Saint Kitts and Nevis', flag: '🇰🇳', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'KW', englishName: 'Kuwait', nativeName: 'Kuwait', flag: '🇰🇼', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'LA', englishName: 'Lao People\'s Democratic Republic', nativeName: 'Lao People\'s Democratic Republic', flag: '🇱🇦', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'LB', englishName: 'Lebanon', nativeName: 'Lebanon', flag: '🇱🇧', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'LR', englishName: 'Liberia', nativeName: 'Liberia', flag: '🇱🇷', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'LY', englishName: 'Libya', nativeName: 'Libya', flag: '🇱🇾', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'LC', englishName: 'Saint Lucia', nativeName: 'Saint Lucia', flag: '🇱🇨', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'LI', englishName: 'Liechtenstein', nativeName: 'Liechtenstein', flag: '🇱🇮', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'LK', englishName: 'Sri Lanka', nativeName: 'Sri Lanka', flag: '🇱🇰', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'LS', englishName: 'Lesotho', nativeName: 'Lesotho', flag: '🇱🇸', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MO', englishName: 'Macao', nativeName: 'Macao', flag: '🇲🇴', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MF', englishName: 'Saint Martin (French part)', nativeName: 'Saint Martin (French part)', flag: '🇲🇫', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MA', englishName: 'Morocco', nativeName: 'Morocco', flag: '🇲🇦', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MC', englishName: 'Monaco', nativeName: 'Monaco', flag: '🇲🇨', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MD', englishName: 'Moldova, Republic of', nativeName: 'Moldova, Republic of', flag: '🇲🇩', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MG', englishName: 'Madagascar', nativeName: 'Madagascar', flag: '🇲🇬', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MV', englishName: 'Maldives', nativeName: 'Maldives', flag: '🇲🇻', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MH', englishName: 'Marshall Islands', nativeName: 'Marshall Islands', flag: '🇲🇭', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MK', englishName: 'North Macedonia', nativeName: 'North Macedonia', flag: '🇲🇰', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'ML', englishName: 'Mali', nativeName: 'Mali', flag: '🇲🇱', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MM', englishName: 'Myanmar', nativeName: 'Myanmar', flag: '🇲🇲', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'ME', englishName: 'Montenegro', nativeName: 'Montenegro', flag: '🇲🇪', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MN', englishName: 'Mongolia', nativeName: 'Mongolia', flag: '🇲🇳', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MP', englishName: 'Northern Mariana Islands', nativeName: 'Northern Mariana Islands', flag: '🇲🇵', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MZ', englishName: 'Mozambique', nativeName: 'Mozambique', flag: '🇲🇿', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MR', englishName: 'Mauritania', nativeName: 'Mauritania', flag: '🇲🇷', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MS', englishName: 'Montserrat', nativeName: 'Montserrat', flag: '🇲🇸', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MQ', englishName: 'Martinique', nativeName: 'Martinique', flag: '🇲🇶', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MU', englishName: 'Mauritius', nativeName: 'Mauritius', flag: '🇲🇺', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MW', englishName: 'Malawi', nativeName: 'Malawi', flag: '🇲🇼', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'MY', englishName: 'Malaysia', nativeName: 'Malaysia', flag: '🇲🇾', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'YT', englishName: 'Mayotte', nativeName: 'Mayotte', flag: '🇾🇹', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'NA', englishName: 'Namibia', nativeName: 'Namibia', flag: '🇳🇦', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'NC', englishName: 'New Caledonia', nativeName: 'New Caledonia', flag: '🇳🇨', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'NE', englishName: 'Niger', nativeName: 'Niger', flag: '🇳🇪', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'NF', englishName: 'Norfolk Island', nativeName: 'Norfolk Island', flag: '🇳🇫', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'NG', englishName: 'Nigeria', nativeName: 'Nigeria', flag: '🇳🇬', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'NI', englishName: 'Nicaragua', nativeName: 'Nicaragua', flag: '🇳🇮', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'NU', englishName: 'Niue', nativeName: 'Niue', flag: '🇳🇺', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'NP', englishName: 'Nepal', nativeName: 'Nepal', flag: '🇳🇵', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'NR', englishName: 'Nauru', nativeName: 'Nauru', flag: '🇳🇷', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'OM', englishName: 'Oman', nativeName: 'Oman', flag: '🇴🇲', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'PK', englishName: 'Pakistan', nativeName: 'Pakistan', flag: '🇵🇰', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'PA', englishName: 'Panama', nativeName: 'Panama', flag: '🇵🇦', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'PN', englishName: 'Pitcairn', nativeName: 'Pitcairn', flag: '🇵🇳', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'PE', englishName: 'Peru', nativeName: 'Peru', flag: '🇵🇪', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'PH', englishName: 'Philippines', nativeName: 'Philippines', flag: '🇵🇭', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'PW', englishName: 'Palau', nativeName: 'Palau', flag: '🇵🇼', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'PG', englishName: 'Papua New Guinea', nativeName: 'Papua New Guinea', flag: '🇵🇬', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'PR', englishName: 'Puerto Rico', nativeName: 'Puerto Rico', flag: '🇵🇷', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'KP', englishName: 'Korea, Democratic People\'s Republic of', nativeName: 'Korea, Democratic People\'s Republic of', flag: '🇰🇵', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'PY', englishName: 'Paraguay', nativeName: 'Paraguay', flag: '🇵🇾', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'PS', englishName: 'Palestine, State of', nativeName: 'Palestine, State of', flag: '🇵🇸', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'PF', englishName: 'French Polynesia', nativeName: 'French Polynesia', flag: '🇵🇫', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'QA', englishName: 'Qatar', nativeName: 'Qatar', flag: '🇶🇦', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'RE', englishName: 'Réunion', nativeName: 'Réunion', flag: '🇷🇪', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'RU', englishName: 'Russian Federation', nativeName: 'Russian Federation', flag: '🇷🇺', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'RW', englishName: 'Rwanda', nativeName: 'Rwanda', flag: '🇷🇼', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'SA', englishName: 'Saudi Arabia', nativeName: 'Saudi Arabia', flag: '🇸🇦', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'SD', englishName: 'Sudan', nativeName: 'Sudan', flag: '🇸🇩', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'SN', englishName: 'Senegal', nativeName: 'Senegal', flag: '🇸🇳', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'GS', englishName: 'South Georgia and the South Sandwich Islands', nativeName: 'South Georgia and the South Sandwich Islands', flag: '🇬🇸', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'SH', englishName: 'Saint Helena, Ascension and Tristan da Cunha', nativeName: 'Saint Helena, Ascension and Tristan da Cunha', flag: '🇸🇭', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'SJ', englishName: 'Svalbard and Jan Mayen', nativeName: 'Svalbard and Jan Mayen', flag: '🇸🇯', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'SB', englishName: 'Solomon Islands', nativeName: 'Solomon Islands', flag: '🇸🇧', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'SL', englishName: 'Sierra Leone', nativeName: 'Sierra Leone', flag: '🇸🇱', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'SV', englishName: 'El Salvador', nativeName: 'El Salvador', flag: '🇸🇻', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'SM', englishName: 'San Marino', nativeName: 'San Marino', flag: '🇸🇲', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'SO', englishName: 'Somalia', nativeName: 'Somalia', flag: '🇸🇴', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'PM', englishName: 'Saint Pierre and Miquelon', nativeName: 'Saint Pierre and Miquelon', flag: '🇵🇲', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'RS', englishName: 'Serbia', nativeName: 'Serbia', flag: '🇷🇸', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'SS', englishName: 'South Sudan', nativeName: 'South Sudan', flag: '🇸🇸', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'ST', englishName: 'Sao Tome and Principe', nativeName: 'Sao Tome and Principe', flag: '🇸🇹', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'SR', englishName: 'Suriname', nativeName: 'Suriname', flag: '🇸🇷', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'SZ', englishName: 'Eswatini', nativeName: 'Eswatini', flag: '🇸🇿', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'SX', englishName: 'Sint Maarten (Dutch part)', nativeName: 'Sint Maarten (Dutch part)', flag: '🇸🇽', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'SC', englishName: 'Seychelles', nativeName: 'Seychelles', flag: '🇸🇨', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'SY', englishName: 'Syrian Arab Republic', nativeName: 'Syrian Arab Republic', flag: '🇸🇾', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'TC', englishName: 'Turks and Caicos Islands', nativeName: 'Turks and Caicos Islands', flag: '🇹🇨', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'TD', englishName: 'Chad', nativeName: 'Chad', flag: '🇹🇩', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'TG', englishName: 'Togo', nativeName: 'Togo', flag: '🇹🇬', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'TH', englishName: 'Thailand', nativeName: 'Thailand', flag: '🇹🇭', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'TJ', englishName: 'Tajikistan', nativeName: 'Tajikistan', flag: '🇹🇯', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'TK', englishName: 'Tokelau', nativeName: 'Tokelau', flag: '🇹🇰', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'TM', englishName: 'Turkmenistan', nativeName: 'Turkmenistan', flag: '🇹🇲', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'TL', englishName: 'Timor-Leste', nativeName: 'Timor-Leste', flag: '🇹🇱', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'TO', englishName: 'Tonga', nativeName: 'Tonga', flag: '🇹🇴', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'TT', englishName: 'Trinidad and Tobago', nativeName: 'Trinidad and Tobago', flag: '🇹🇹', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'TN', englishName: 'Tunisia', nativeName: 'Tunisia', flag: '🇹🇳', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'TR', englishName: 'Türkiye', nativeName: 'Türkiye', flag: '🇹🇷', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'TV', englishName: 'Tuvalu', nativeName: 'Tuvalu', flag: '🇹🇻', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'TW', englishName: 'Taiwan, Province of China', nativeName: 'Taiwan, Province of China', flag: '🇹🇼', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'TZ', englishName: 'Tanzania, United Republic of', nativeName: 'Tanzania, United Republic of', flag: '🇹🇿', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'UG', englishName: 'Uganda', nativeName: 'Uganda', flag: '🇺🇬', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'UM', englishName: 'United States Minor Outlying Islands', nativeName: 'United States Minor Outlying Islands', flag: '🇺🇲', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'UY', englishName: 'Uruguay', nativeName: 'Uruguay', flag: '🇺🇾', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'UZ', englishName: 'Uzbekistan', nativeName: 'Uzbekistan', flag: '🇺🇿', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'VA', englishName: 'Holy See (Vatican City State)', nativeName: 'Holy See (Vatican City State)', flag: '🇻🇦', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'VC', englishName: 'Saint Vincent and the Grenadines', nativeName: 'Saint Vincent and the Grenadines', flag: '🇻🇨', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'VE', englishName: 'Venezuela, Bolivarian Republic of', nativeName: 'Venezuela, Bolivarian Republic of', flag: '🇻🇪', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'VG', englishName: 'Virgin Islands, British', nativeName: 'Virgin Islands, British', flag: '🇻🇬', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'VI', englishName: 'Virgin Islands, U.S.', nativeName: 'Virgin Islands, U.S.', flag: '🇻🇮', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'VN', englishName: 'Viet Nam', nativeName: 'Viet Nam', flag: '🇻🇳', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'VU', englishName: 'Vanuatu', nativeName: 'Vanuatu', flag: '🇻🇺', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'WF', englishName: 'Wallis and Futuna', nativeName: 'Wallis and Futuna', flag: '🇼🇫', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'WS', englishName: 'Samoa', nativeName: 'Samoa', flag: '🇼🇸', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'YE', englishName: 'Yemen', nativeName: 'Yemen', flag: '🇾🇪', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'ZA', englishName: 'South Africa', nativeName: 'South Africa', flag: '🇿🇦', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'ZM', englishName: 'Zambia', nativeName: 'Zambia', flag: '🇿🇲', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
  { code: 'ZW', englishName: 'Zimbabwe', nativeName: 'Zimbabwe', flag: '🇿🇼', primaryLocale: 'en', currency: 'USD', currencySymbol: '$', dateFormat: 'DMY', decimalSeparator: '.' },
];

/** EU member states (relevant for eurozone / customs / VAT features). */
const EU_MEMBERS = new Set<SupportedCountry>([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
]);

/** Eurozone countries (use EUR). */
const EUROZONE = new Set<SupportedCountry>([
  'AT', 'BE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LU', 'MT',
  'NL', 'PT', 'SK', 'SI', 'ES',
]);

/** Countries that drive on the left. */
const LEFT_HAND_TRAFFIC = new Set<SupportedCountry>(['GB', 'AU', 'NZ', 'JP', 'IN', 'SG']);

/** Countries using imperial measurement system. */
const IMPERIAL_COUNTRIES = new Set<SupportedCountry>(['US', 'GB']);

/** Countries where the week starts on Sunday. */
const SUNDAY_WEEK_START = new Set<SupportedCountry>(['US', 'CA', 'JP', 'BR', 'MX', 'AR', 'CL']);

/**
 * Derive CountryFeatures for a given country code.
 * This is pure, deterministic, and respects explicit user overrides.
 */
export function getCountryFeatures(code: SupportedCountry, overrideFirstDay?: 0 | 1): CountryFeatures {
  const userFirstDay = overrideFirstDay !== undefined ? overrideFirstDay : getStoredFirstDayOfWeek();
  return {
    ksef: code === 'PL',
    eurozone: EUROZONE.has(code),
    euMember: EU_MEMBERS.has(code),
    leftHandTraffic: LEFT_HAND_TRAFFIC.has(code),
    measurementSystem: IMPERIAL_COUNTRIES.has(code) ? 'imperial' : 'metric',
    firstDayOfWeek: userFirstDay !== undefined ? userFirstDay : (SUNDAY_WEEK_START.has(code) ? 0 : 1),
  };
}

/**
 * Build a full CountryContext for the given country code.
 */
export function buildCountryContext(code: SupportedCountry): CountryContext {
  const country = SUPPORTED_COUNTRIES.find((c) => c.code === code);
  if (!country) throw new Error(`Unknown country code: ${code}`);
  return { country, features: getCountryFeatures(code) };
}

// ── Persistence ───────────────────────────────────────────────────────────────

/** localStorage key used to persist the user's country preference. */
export const COUNTRY_STORAGE_KEY = 'yada:country';

/** localStorage key used to persist the user's first day of week preference (0=Sunday, 1=Monday, undefined=auto). */
export const FIRST_DAY_OF_WEEK_STORAGE_KEY = 'yada:first_day_of_week';

export type FirstDayOfWeekPreference = 'auto' | 0 | 1;

/** Default country when no preference is stored. */
export const DEFAULT_COUNTRY: SupportedCountry = 'GB';

/**
 * Read the stored country preference.
 * Returns undefined if nothing is stored (e.g., user hasn't set it yet).
 */
export function getStoredCountry(): SupportedCountry | undefined {
  try {
    const raw = localStorage.getItem(COUNTRY_STORAGE_KEY);
    if (!raw) return undefined;
    const isValid = SUPPORTED_COUNTRIES.some((c) => c.code === raw);
    return isValid ? (raw as SupportedCountry) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Read the stored first day of week preference.
 * Returns undefined if set to 'auto' or unset.
 */
export function getStoredFirstDayOfWeek(): 0 | 1 | undefined {
  try {
    const raw = localStorage.getItem(FIRST_DAY_OF_WEEK_STORAGE_KEY);
    if (raw === '0') return 0;
    if (raw === '1') return 1;
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Read the raw first day of week preference ('auto', 0, 1).
 */
export function getFirstDayOfWeekPreference(): FirstDayOfWeekPreference {
  const stored = getStoredFirstDayOfWeek();
  return stored !== undefined ? stored : 'auto';
}

/** Custom DOM event name emitted when the country or week start preference changes. */
export const COUNTRY_CHANGE_EVENT = 'yada:country-change';

/**
 * Persist the first day of week preference.
 * Dispatches a 'yada:country-change' DOM CustomEvent so all mounted apps and runtime update.
 * Returns the new CountryContext.
 */
export function setStoredFirstDayOfWeek(pref: FirstDayOfWeekPreference): CountryContext {
  try {
    if (pref === 'auto') {
      localStorage.removeItem(FIRST_DAY_OF_WEEK_STORAGE_KEY);
    } else {
      localStorage.setItem(FIRST_DAY_OF_WEEK_STORAGE_KEY, String(pref));
    }
  } catch {
    // Silently ignore storage errors (private browsing, quota exceeded)
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(COUNTRY_CHANGE_EVENT, { detail: { firstDayOfWeek: pref } }));
  }
  const country = getStoredCountry() ?? DEFAULT_COUNTRY;
  return buildCountryContext(country);
}

/**
 * Persist the country preference to localStorage.
 * Dispatches a 'yada:country-change' DOM CustomEvent so the runtime can
 * broadcast the updated locale context to all mounted apps without coupling
 * the i18n package to the runtime directly.
 * Returns the new CountryContext.
 */
export function setStoredCountry(code: SupportedCountry): CountryContext {
  try {
    localStorage.setItem(COUNTRY_STORAGE_KEY, code);
  } catch {
    // Silently ignore storage errors (private browsing, quota exceeded)
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(COUNTRY_CHANGE_EVENT, { detail: { code } }));
  }
  return buildCountryContext(code);
}

/**
 * Get the current country context.
 * Falls back to DEFAULT_COUNTRY if nothing is stored.
 */
export function getCurrentCountryContext(): CountryContext {
  const stored = getStoredCountry();
  return buildCountryContext(stored ?? DEFAULT_COUNTRY);
}
